import React from 'react';
import { OverlayTrigger, Tooltip } from '@/components/ui';
import type { UnitFlag, UnitFlagKey } from '../../../types/Vehicle';

/** One icon per flag kind (ICON-01); the meaning is always also in text (COL-04). */
const FLAG_ICON: Record<UnitFlagKey, string> = {
  pmOverdue: 'bi-wrench-adjustable',
  greasingOverdue: 'bi-droplet',
  cvipOverdue: 'bi-clipboard-x',
  cvipDueSoon: 'bi-clipboard-check',
  criticalDefects: 'bi-exclamation-octagon',
  openDefects: 'bi-exclamation-triangle',
  insuranceExpired: 'bi-shield-x',
  insuranceMissing: 'bi-shield-exclamation',
  notVisibleToDrivers: 'bi-eye-slash',
  pastExpectedReturn: 'bi-calendar-x'
};

const TONE: Record<UnitFlag['tone'], string> = {
  danger: 'tw-border-danger/40 tw-bg-danger/10 tw-text-danger',
  warning: 'tw-border-warning/50 tw-bg-warning/15 tw-text-warning-text',
  neutral: 'tw-border-line-input tw-bg-surface-alt tw-text-content-secondary'
};

interface EquipmentFlagsProps {
  flags: UnitFlag[] | undefined;
  /** `compact` shows icons (and the defect count) for tables and cards; `full` adds the label. */
  variant?: 'compact' | 'full';
  /** Flags not to show here, e.g. a header that already shows availability. */
  omit?: UnitFlagKey[];
  /** Wrap onto more lines (cards, header) or stay on one line (table cells). */
  wrap?: boolean;
  className?: string;
}

/**
 * A unit's flags (EQ-15) with a tap-readable tooltip each (TCH-03), e.g. "PM overdue by 118 km".
 * The flags come from the server, so the Fleet list, Shop board and unit header agree.
 */
const EquipmentFlags: React.FC<EquipmentFlagsProps> = ({ flags, variant = 'compact', omit = [], wrap = true, className = '' }) => {
  const shown = (flags ?? []).filter((flag) => !omit.includes(flag.key));
  if (shown.length === 0) return null;
  return (
    <ul className={`tw-m-0 tw-flex tw-list-none ${wrap ? 'tw-flex-wrap' : 'tw-flex-nowrap'} tw-items-center tw-gap-1 tw-p-0 ${className}`} aria-label="Flags">
      {shown.map((flag) => (
        <li key={flag.key}>
          <OverlayTrigger
            placement="top"
            trigger={['hover', 'focus', 'click']}
            overlay={<Tooltip id={`flag-${flag.key}`}>{flag.detail}</Tooltip>}
          >
            <span
              tabIndex={0}
              role="img"
              aria-label={flag.detail}
              className={`tw-inline-flex tw-h-6 tw-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-full tw-border tw-px-2 tw-text-xs tw-font-semibold ${TONE[flag.tone]}`}
            >
              <i className={`bi ${FLAG_ICON[flag.key]}`} aria-hidden="true" />
              {variant === 'full' && flag.key !== 'openDefects' && <span>{flag.label}</span>}
              {flag.key === 'openDefects' && <span className="tw-tabular-nums">{flag.label}</span>}
            </span>
          </OverlayTrigger>
        </li>
      ))}
    </ul>
  );
};

export default EquipmentFlags;
